/**
 * Shares.tsx — Phase 6 更新
 * 新增：上传链接标签页 + 创建上传链接功能
 */

import { useI18n } from '../i18n';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { directLinkApi, shareApi } from '@/services/api';
import { formatBytes, formatDate } from '@/utils';
import { Button } from '@/components/ui/Button';
import { FileIcon } from '@/components/files/FileIcon';
import { UploadLinkDialog } from '@/components/files/dialogs';
import { FolderPickerDialog } from '@/components/files/dialogs';
import { useToast } from '@/components/ui/useToast';
import {
  Link2,
  Trash2,
  Lock,
  Clock,
  Download,
  AlertCircle,
  CheckCircle2,
  Ban,
  ExternalLink,
  Upload,
  Copy,
} from 'lucide-react';
import { cn } from '@/utils';

type Tab = 'download' | 'direct' | 'upload';

export default function Shares() {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('download');
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [uploadFolderMeta, setUploadFolderMeta] = useState<{ id: string; name: string } | null>(null);

  const { data: shares = [], isLoading } = useQuery({
    queryKey: ['shares'],
    queryFn: () => shareApi.list().then((res) => res.data.data ?? []),
  });

  const {
    data: directLinks = [],
    isLoading: directLinksLoading,
    isError: directLinksError,
    refetch: refetchDirectLinks,
  } = useQuery({
    queryKey: ['direct-links'],
    queryFn: () => directLinkApi.list().then((res) => res.data.data ?? []),
    staleTime: 0,
  });

  const deleteDirectLinkMutation = useMutation({
    mutationFn: (fileId: string) => directLinkApi.delete(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['direct-links'] });
      toast({ title: t("直链已删除") });
    },
    onError: () => toast({ title: t("删除直链失败"), variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => shareApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      toast({ title: t("已删除") });
    },
    onError: () => toast({ title: t("删除失败"), variant: 'destructive' }),
  });

  const createUploadLinkMutation = useMutation({
    mutationFn: (params: Parameters<typeof shareApi.createUploadLink>[0]) => shareApi.createUploadLink(params),
    onSuccess: (res) => {
      const d = res.data.data;
      if (d?.uploadToken) {
        const url = `${window.location.origin}/upload/${d.uploadToken}`;
        navigator.clipboard.writeText(url).then(() => toast({ title: t("上传链接已复制"), description: url }));
      }
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      setShowFolderPicker(false);
      setUploadFolderMeta(null);
    },
    onError: () => toast({ title: t("创建失败"), variant: 'destructive' }),
  });

  const handleCopyLink = (shareId: string, isUploadLink = false, uploadToken?: string) => {
    const url =
      isUploadLink && uploadToken
        ? `${window.location.origin}/upload/${uploadToken}`
        : `${window.location.origin}/share/${shareId}`;
    navigator.clipboard.writeText(url).then(() => toast({ title: t("链接已复制到剪贴板") }));
  };

  const getStatus = (share: any): 'active' | 'expired' | 'exhausted' => {
    if (share.expiresAt && !(Date.parse(share.expiresAt) > Date.now())) return 'expired';
    if (!share.isUploadLink && share.downloadLimit && share.downloadCount >= share.downloadLimit) return 'exhausted';
    if (share.isUploadLink && share.maxUploadCount != null && share.uploadCount >= share.maxUploadCount)
      return 'exhausted';
    return 'active';
  };

  const downloadShares = shares.filter((s: any) => !s.isUploadLink);
  const uploadShares = shares.filter((s: any) => s.isUploadLink);
  const activeDownload = downloadShares.filter((s: any) => getStatus(s) === 'active');
  const activeUpload = uploadShares.filter((s: any) => getStatus(s) === 'active');
  const directShares = directLinks.map((link) => ({
    id: link.fileId,
    directUrl: link.directUrl,
    expiresAt: link.expiresAt,
    file: { name: link.fileName, size: link.size, mimeType: link.mimeType, isFolder: false },
  }));
  const activeDirect = directShares.filter((share) => getStatus(share) === 'active');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold">{t("分享管理")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('{{p0}} 个有效下载分享 · {{p1}} 个有效上传链接', { p0: activeDownload.length, p1: activeUpload.length })}{' · '}
            {directLinksError ? t("直链加载失败") : t("{{p0}} 个有效直链", { p0: activeDirect.length })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto">
        {(
          [
            { key: 'download' as Tab, label: t("下载分享"), count: downloadShares.length, icon: Link2 },
            { key: 'direct' as Tab, label: t("直链分享"), count: directShares.length, icon: ExternalLink },
            { key: 'upload' as Tab, label: t("上传链接"), count: uploadShares.length, icon: Upload },
          ] as const
        ).map(({ key, label, count, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            {count > 0 && (
              <span className="text-xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Download shares tab */}
      {tab === 'download' && (
        <ShareList
          shares={downloadShares}
          isLoading={isLoading}
          emptyText={t("暂无下载分享")}
          emptyHint={t("在文件管理页面右键文件即可创建")}
          getStatus={getStatus}
          onCopy={(id) => handleCopyLink(id)}
          onDelete={(id) => {
            if (confirm(t("确定要删除这个分享链接吗？"))) deleteMutation.mutate(id);
          }}
          onBulkDelete={(ids) => {
            if (confirm(t("删除 {{p0}} 个失效链接？", { p0: ids.length }))) ids.forEach((id) => deleteMutation.mutate(id));
          }}
          isPending={deleteMutation.isPending}
        />
      )}

      {tab === 'direct' &&
        (directLinksError ? (
          <div className="text-center py-16 text-muted-foreground space-y-3" role="alert">
            <p>{t("直链加载失败")}</p>
            <Button variant="outline" size="sm" onClick={() => refetchDirectLinks()}>
              {t("重试")}</Button>
          </div>
        ) : (
          <ShareList
            shares={directShares}
            isLoading={directLinksLoading}
            emptyText={t("暂无直链分享")}
            emptyHint={t("在文件管理页面右键文件，选择「直链」即可创建")}
            getStatus={getStatus}
            isDirectTab
            onCopy={(_id, share) => {
              navigator.clipboard
                .writeText(share.directUrl)
                .then(() => toast({ title: t("直链已复制到剪贴板") }))
                .catch(() => toast({ title: t("复制失败，请重试"), variant: 'destructive' }));
            }}
            onDelete={(id) => {
              if (confirm(t("确定要删除这个直链吗？原链接将立即失效。"))) deleteDirectLinkMutation.mutate(id);
            }}
            onBulkDelete={(ids) => {
              if (confirm(t("删除 {{p0}} 个失效直链？", { p0: ids.length }))) ids.forEach((id) => deleteDirectLinkMutation.mutate(id));
            }}
            isPending={deleteDirectLinkMutation.isPending}
          />
        ))}

      {/* Upload links tab */}
      {tab === 'upload' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowFolderPicker(true)}>
              <Upload className="h-3.5 w-3.5 mr-1.5" /> {t("创建上传链接")}</Button>
          </div>
          <ShareList
            shares={uploadShares}
            isLoading={isLoading}
            emptyText={t("暂无上传链接")}
            emptyHint={t("创建上传链接后，任何人无需登录即可上传文件到指定文件夹")}
            getStatus={getStatus}
            isUploadTab
            onCopy={(id, share) => handleCopyLink(id, true, share?.uploadToken)}
            onDelete={(id) => {
              if (confirm(t("确定要删除这个上传链接吗？"))) deleteMutation.mutate(id);
            }}
            onBulkDelete={(ids) => {
              if (confirm(t("删除 {{p0}} 个失效链接？", { p0: ids.length }))) ids.forEach((id) => deleteMutation.mutate(id));
            }}
            isPending={deleteMutation.isPending}
          />
        </div>
      )}

      {/* Step 1: Pick a folder */}
      {showFolderPicker && (
        <FolderPickerDialog
          title={t("选择目标文件夹")}
          confirmLabel={t("下一步：设置链接")}
          onConfirm={(id, name) => {
            setUploadFolderMeta({ id, name });
            setShowFolderPicker(false);
          }}
          onCancel={() => setShowFolderPicker(false)}
        />
      )}

      {/* Step 2: Configure upload link */}
      {!showFolderPicker && uploadFolderMeta && (
        <UploadLinkDialog
          folderId={uploadFolderMeta.id}
          folderName={uploadFolderMeta.name}
          isPending={createUploadLinkMutation.isPending}
          onConfirm={(params) => createUploadLinkMutation.mutate({ folderId: uploadFolderMeta.id, ...params })}
          onCancel={() => setUploadFolderMeta(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShareList
// ─────────────────────────────────────────────────────────────────────────────
interface ShareListProps {
  shares: any[];
  isLoading: boolean;
  emptyText: string;
  emptyHint: string;
  getStatus: (s: any) => 'active' | 'expired' | 'exhausted';
  isUploadTab?: boolean;
  isDirectTab?: boolean;
  onCopy: (id: string, share?: any) => void;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => void;
  isPending: boolean;
}

function ShareList({
  shares,
  isLoading,
  emptyText,
  emptyHint,
  getStatus,
  isUploadTab,
  isDirectTab,
  onCopy,
  onDelete,
  onBulkDelete,
  isPending,
}: ShareListProps) {
  const { t } = useI18n();
  const activeShares = shares.filter((s) => getStatus(s) === 'active');
  const inactiveShares = shares.filter((s) => getStatus(s) !== 'active');

  if (isLoading) return <div className="text-center py-16 text-muted-foreground">{t("加载中...")}</div>;

  if (shares.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground space-y-3">
        {isUploadTab ? (
          <Upload className="h-14 w-14 mx-auto opacity-20" />
        ) : (
          <Link2 className="h-14 w-14 mx-auto opacity-20" />
        )}
        <p className="font-medium">{emptyText}</p>
        <p className="text-sm">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activeShares.length > 0 && (
        <section className="space-y-1.5">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("有效")}</h2>
          <div className="bg-card border rounded-xl overflow-hidden divide-y">
            {activeShares.map((share) => (
              <ShareItem
                key={share.id}
                share={share}
                status="active"
                isUploadTab={isUploadTab}
                isDirectTab={isDirectTab}
                onCopy={onCopy}
                onDelete={onDelete}
                isPending={isPending}
              />
            ))}
          </div>
        </section>
      )}
      {inactiveShares.length > 0 && (
        <section className="space-y-1.5">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("已失效")}</h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs text-muted-foreground hover:text-red-500"
              onClick={() => onBulkDelete(inactiveShares.map((s) => s.id))}
            >
              <Trash2 className="h-3 w-3 mr-1" /> {t("清理失效")}</Button>
          </div>
          <div className="bg-card border rounded-xl overflow-hidden divide-y opacity-60">
            {inactiveShares.map((share) => (
              <ShareItem
                key={share.id}
                share={share}
                status={getStatus(share)}
                isUploadTab={isUploadTab}
                isDirectTab={isDirectTab}
                onCopy={onCopy}
                onDelete={onDelete}
                isPending={isPending}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShareItem
// ─────────────────────────────────────────────────────────────────────────────
interface ShareItemProps {
  share: any;
  status: 'active' | 'expired' | 'exhausted';
  isUploadTab?: boolean;
  isDirectTab?: boolean;
  onCopy: (id: string, share?: any) => void;
  onDelete: (id: string) => void;
  isPending?: boolean;
}

function ShareItem({ share, status, isUploadTab, isDirectTab, onCopy, onDelete, isPending }: ShareItemProps) {
  const { t } = useI18n();
  const statusConfig = {
    active: { icon: CheckCircle2, label: t("有效"), color: 'text-emerald-500' },
    expired: { icon: AlertCircle, label: t("已过期"), color: 'text-amber-500' },
    exhausted: { icon: Ban, label: t("次数已满"), color: 'text-red-500' },
  };
  const { icon: StatusIcon, label, color } = statusConfig[status];

  const publicUrl = isDirectTab
    ? share.directUrl
    : isUploadTab && share.uploadToken
      ? `${window.location.origin}/upload/${share.uploadToken}`
      : `${window.location.origin}/share/${share.id}`;

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 group hover:bg-accent/30 transition-colors">
      <div className="flex-shrink-0">
        <FileIcon mimeType={share.file?.mimeType} isFolder={share.file?.isFolder} size="md" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm truncate">{share.file?.name ?? t("未知文件夹")}</p>
          <span className={cn('flex items-center gap-0.5 text-xs flex-shrink-0', color)}>
            <StatusIcon className="h-3 w-3" /> {label}
          </span>
          {isUploadTab && (
            <span className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded px-1.5 py-0.5">
              {t("上传链接")}</span>
          )}
          {share.file?.isFolder && !isUploadTab && (
            <span className="text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded px-1.5 py-0.5">
              {t("文件夹")}</span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
          {!isDirectTab && <span>{t("创建 {{p0}}", { p0: formatDate(share.createdAt) })}</span>}
          {isDirectTab && !share.expiresAt && <span>{t("永久有效")}</span>}
          {share.file && !share.file.isFolder && <span>{formatBytes(share.file.size)}</span>}
          {share.expiresAt && (
            <span className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" />
              {status === 'expired' ? t("已过期于") : t("过期")} {formatDate(share.expiresAt)}
            </span>
          )}
          {share.password && (
            <span className="flex items-center gap-0.5">
              <Lock className="h-3 w-3" /> {t("有密码")}</span>
          )}
          {!isUploadTab && share.downloadLimit != null && (
            <span className="flex items-center gap-0.5">
              <Download className="h-3 w-3" />
              {t("{{p0}} / {{p1}} 次", { p0: share.downloadCount, p1: share.downloadLimit })}</span>
          )}
          {isUploadTab && share.maxUploadCount != null && (
            <span className="flex items-center gap-0.5">
              <Upload className="h-3 w-3" />
              {t("{{p0}} / {{p1}} 次", { p0: share.uploadCount, p1: share.maxUploadCount })}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity touch-visible">
        {status === 'active' && (
          <>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => onCopy(share.id, share)}>
              <Copy className="h-3 w-3" /> {t("复制链接")}</Button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="icon" className="h-7 w-7" title={t("在新标签页打开")}>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 hover:bg-red-500/10 hover:text-red-500"
          onClick={() => onDelete(share.id)}
          disabled={isPending}
          title={t("删除")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
